import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Plus,
  Upload,
  Search,
  Download,
  Filter,
  Pencil,
  Trash2,
  CalendarPlus,
  X,
  Flag,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PRIORITIES, PRIORITY_CONFIG, PriorityBadge } from "@/lib/priority";

const STATUSES = ["New", "Messaged", "Responded", "Registered", "Confirmed", "Attended", "No-Show"];
const PAGE_SIZES = [20, 50, 100, 500];
const STATUS_TONES = {
  New: "bg-muted text-foreground/70",
  Messaged: "bg-fun-blue-tint text-fun-blue",
  Responded: "bg-fun-yellow-tint text-fun-yellow",
  Registered: "bg-fun-orange-tint text-fun-orange",
  Confirmed: "bg-fun-aqua-tint text-fun-aqua",
  Attended: "bg-primary text-primary-foreground",
  "No-Show": "bg-destructive/10 text-destructive",
};

export default function Leads() {
  const [params, setParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [events, setEvents] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const [eventId, setEventId] = useState(params.get("event_id") || "all");
  const [status, setStatus] = useState(params.get("status") || "all");
  const [priorityFilter, setPriorityFilter] = useState(params.get("priority") || "all");
  const [page, setPage] = useState(Number(params.get("page")) || 1);
  const [pageSize, setPageSize] = useState(
    PAGE_SIZES.includes(Number(params.get("page_size"))) ? Number(params.get("page_size")) : 50,
  );
  const [total, setTotal] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null); // lead being edited, null = adding
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", source: "manual", event_id: "", priority: "medium" });
  const [file, setFile] = useState(null);
  const [importEvent, setImportEvent] = useState("");
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);

  const [selected, setSelected] = useState(new Set());
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignEvent, setAssignEvent] = useState("");
  const [assignStatus, setAssignStatus] = useState("New");
  const [assigning, setAssigning] = useState(false);
  const [priorityOpen, setPriorityOpen] = useState(false);
  const [bulkPriority, setBulkPriority] = useState("high");
  const [settingPriority, setSettingPriority] = useState(false);

  async function loadEvents() {
    const { data } = await api.get("/events");
    setEvents(data);
  }
  async function loadLeads() {
    const p = { page, page_size: pageSize };
    if (q) p.q = q;
    if (eventId === "unassigned") {
      p.unassigned_only = true;
    } else if (eventId && eventId !== "all") {
      p.event_id = eventId;
    }
    if (status && status !== "all") p.status = status;
    if (priorityFilter && priorityFilter !== "all") p.priority = priorityFilter;
    if (status && status !== "all" && (!eventId || eventId === "all")) return; // status requires event
    const { data } = await api.get("/leads", { params: p });
    // A delete can empty out the page we're sitting on (e.g. deleting the
    // last lead on the last page) — step back a page rather than show blank.
    if (data.items.length === 0 && data.total > 0 && page > 1) {
      setPage(Math.max(1, Math.ceil(data.total / pageSize)));
      return;
    }
    setLeads(data.items);
    setTotal(data.total);
    // A reload can change which rows exist (filter change, delete, import) —
    // stale ids in the selection would silently no-op on the next bulk action.
    setSelected(new Set());
  }

  useEffect(() => {
    loadEvents();
  }, []);
  // Any filter change invalidates the current page's meaning — jump back to
  // page 1 rather than showing a now-mismatched page of a smaller result set.
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, eventId, status, priorityFilter, pageSize]);
  useEffect(() => {
    const t = setTimeout(loadLeads, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, eventId, status, priorityFilter, page, pageSize]);
  useEffect(() => {
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (eventId && eventId !== "all") next.set("event_id", eventId); // includes "unassigned"
    if (status && status !== "all") next.set("status", status);
    if (priorityFilter && priorityFilter !== "all") next.set("priority", priorityFilter);
    if (page > 1) next.set("page", String(page));
    if (pageSize !== 50) next.set("page_size", String(pageSize));
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, eventId, status, priorityFilter, page, pageSize]);

  function startAdd() {
    setEditing(null);
    setForm({ name: "", phone: "", source: "manual", event_id: "", priority: "medium" });
    setAddOpen(true);
  }

  function startEdit(lead) {
    setEditing(lead);
    setForm({
      name: lead.name || "",
      phone: lead.phone || "",
      source: lead.source || "manual",
      event_id: "",
      priority: lead.priority || "medium",
    });
    setAddOpen(true);
  }

  async function submitAdd(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        // Programme membership is managed per-event, not on the lead record.
        const { name, phone, source, priority } = form;
        await api.patch(`/leads/${editing.id}`, { name, phone, source, priority });
        toast.success("Lead updated");
      } else {
        const payload = { ...form };
        if (!payload.event_id) delete payload.event_id;
        await api.post("/leads", payload);
        toast.success("Lead added");
      }
      setAddOpen(false);
      setEditing(null);
      setForm({ name: "", phone: "", source: "manual", event_id: "", priority: "medium" });
      loadLeads();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function removeLead(lead) {
    try {
      await api.delete(`/leads/${lead.id}`);
      toast.success(`${lead.name} deleted`);
      loadLeads();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function bulkDelete() {
    try {
      const { data } = await api.post("/leads/bulk-delete", {
        lead_ids: Array.from(selected),
      });
      toast.success(`Deleted ${data.deleted} lead${data.deleted === 1 ? "" : "s"}`);
      loadLeads();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  function toggleSelected(leadId) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelected((prev) =>
      prev.size === filtered.length ? new Set() : new Set(filtered.map((l) => l.id)),
    );
  }

  function openAssign() {
    setAssignEvent(eventId !== "all" && eventId !== "unassigned" ? eventId : "");
    setAssignStatus("New");
    setAssignOpen(true);
  }

  async function submitAssign(e) {
    e.preventDefault();
    if (!assignEvent) return toast.error("Choose a programme");
    setAssigning(true);
    try {
      const { data } = await api.post("/leads/bulk-assign", {
        lead_ids: Array.from(selected),
        event_id: assignEvent,
        status: assignStatus,
      });
      toast.success(`Added ${data.assigned} lead${data.assigned === 1 ? "" : "s"} to the programme`);
      setAssignOpen(false);
      loadLeads();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAssigning(false);
    }
  }

  function openPriority() {
    setBulkPriority("high");
    setPriorityOpen(true);
  }

  async function submitPriority(e) {
    e.preventDefault();
    setSettingPriority(true);
    try {
      const { data } = await api.post("/leads/bulk-priority", {
        lead_ids: Array.from(selected),
        priority: bulkPriority,
      });
      toast.success(`Set priority on ${data.updated} lead${data.updated === 1 ? "" : "s"}`);
      setPriorityOpen(false);
      loadLeads();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSettingPriority(false);
    }
  }

  async function submitImport(e) {
    e.preventDefault();
    if (!file) return toast.error("Select a CSV or XLSX file");
    const fd = new FormData();
    fd.append("file", file);
    if (importEvent) fd.append("event_id", importEvent);
    try {
      const { data } = await api.post("/leads/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      toast.success(`Imported ${data.imported}, skipped ${data.skipped}`);
      loadLeads();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  const filtered = useMemo(() => leads, [leads]);

  async function download() {
    const token = localStorage.getItem("wa_crm_token");
    // The CSV export endpoint only understands a real event_id — "unassigned"
    // is a client-side-only filter value, so fall back to exporting everyone
    // rather than sending it a value it can't use.
    const query = eventId && eventId !== "all" && eventId !== "unassigned"
      ? `?event_id=${eventId}`
      : "";
    const url = `${API_BASE}/reports/leads.csv${query}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `leads_${query ? eventId : "all"}.csv`;
    a.click();
  }

  return (
    <div className="mx-auto max-w-7xl p-6 md:p-10" data-testid="leads-page">
      <PageHeader
        overline="Contacts"
        title="Leads"
        subtitle="Manage lead lists, import from Excel/CSV, and track status across every programme."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {selected.size > 0 && (
              <>
                <Button onClick={openAssign} data-testid="leads-bulk-assign-btn">
                  <CalendarPlus className="mr-2 h-4 w-4" /> Add {selected.size} to programme
                </Button>
                <Button
                  variant="outline"
                  onClick={openPriority}
                  data-testid="leads-bulk-priority-btn"
                >
                  <Flag className="mr-2 h-4 w-4" /> Set priority
                </Button>
                <ConfirmDialog
                  testId="leads-bulk-delete-dialog"
                  title={`Delete ${selected.size} lead${selected.size === 1 ? "" : "s"}?`}
                  description={
                    "This removes the selected leads, their whole WhatsApp conversations and " +
                    "their programme links. This cannot be undone."
                  }
                  onConfirm={bulkDelete}
                  trigger={
                    <Button
                      variant="outline"
                      className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                      data-testid="leads-bulk-delete-btn"
                    >
                      <Trash2 className="mr-2 h-4 w-4" /> Delete
                    </Button>
                  }
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSelected(new Set())}
                  title="Clear selection"
                  data-testid="leads-clear-selection-btn"
                >
                  <X className="h-4 w-4" />
                </Button>
              </>
            )}
            <Button variant="outline" onClick={download} data-testid="leads-export-btn">
              <Download className="mr-2 h-4 w-4" /> Export CSV
            </Button>

            <Dialog open={importOpen} onOpenChange={setImportOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="leads-import-btn">
                  <Upload className="mr-2 h-4 w-4" /> Import
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>Import leads</DialogTitle>
                  <DialogDescription>
                    Upload a CSV or XLSX file with lead contact details.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={submitImport} className="space-y-4">
                  <div className="space-y-2">
                    <Label>File (CSV or XLSX)</Label>
                    <Input
                      ref={fileRef}
                      type="file"
                      accept=".csv,.xlsx,.xlsm"
                      onChange={(e) => setFile(e.target.files?.[0] || null)}
                      data-testid="import-file-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Columns: <span className="font-mono">name, phone</span> (optional{" "}
                      <span className="font-mono">source</span>).
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Assign to programme (optional)</Label>
                    <Select value={importEvent} onValueChange={setImportEvent}>
                      <SelectTrigger data-testid="import-event-select">
                        <SelectValue placeholder="None" />
                      </SelectTrigger>
                      <SelectContent>
                        {events.map((e) => (
                          <SelectItem key={e.id} value={e.id}>
                            {e.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {importResult && (
                    <div className="rounded-lg border border-border bg-muted/40 p-3 text-xs">
                      Imported <b>{importResult.imported}</b>, skipped{" "}
                      <b>{importResult.skipped}</b>, errored <b>{importResult.errored}</b>
                    </div>
                  )}
                  <DialogFooter>
                    <Button type="submit" data-testid="import-submit-btn">
                      Upload
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog
              open={addOpen}
              onOpenChange={(v) => {
                setAddOpen(v);
                if (!v) setEditing(null);
              }}
            >
              <DialogTrigger asChild>
                <Button onClick={startAdd} data-testid="new-lead-btn">
                  <Plus className="mr-2 h-4 w-4" /> New lead
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editing ? "Edit lead" : "Add lead"}</DialogTitle>
                  <DialogDescription>
                    {editing
                      ? "Update this lead's details. Programme membership is managed from the programme."
                      : "Add a single lead by name and WhatsApp phone number."}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={submitAdd} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Full name</Label>
                    <Input
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="lead-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>WhatsApp / phone (with country code)</Label>
                    <Input
                      required
                      placeholder="+91 98xxxxxxxx"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      data-testid="lead-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Source</Label>
                    <Input
                      value={form.source}
                      onChange={(e) => setForm({ ...form, source: e.target.value })}
                      data-testid="lead-source-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select
                      value={form.priority}
                      onValueChange={(v) => setForm({ ...form, priority: v })}
                    >
                      <SelectTrigger data-testid="lead-priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORITIES.map((p) => {
                          const cfg = PRIORITY_CONFIG[p];
                          const Icon = cfg.icon;
                          return (
                            <SelectItem key={p} value={p}>
                              <span className="flex items-center gap-1.5">
                                <Icon className="h-3.5 w-3.5" /> {cfg.label}
                              </span>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  {!editing && (
                    <div className="space-y-2">
                      <Label>Programme (optional)</Label>
                      <Select
                        value={form.event_id}
                        onValueChange={(v) => setForm({ ...form, event_id: v })}
                      >
                        <SelectTrigger data-testid="lead-event-select">
                          <SelectValue placeholder="None" />
                        </SelectTrigger>
                        <SelectContent>
                          {events.map((e) => (
                            <SelectItem key={e.id} value={e.id}>
                              {e.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <DialogFooter>
                    <Button type="submit" disabled={saving} data-testid="lead-submit-btn">
                      {saving ? "Saving…" : editing ? "Save changes" : "Add lead"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
              <DialogContent className="sm:max-w-md" data-testid="bulk-assign-dialog">
                <DialogHeader>
                  <DialogTitle>Add {selected.size} lead{selected.size === 1 ? "" : "s"} to a programme</DialogTitle>
                  <DialogDescription>
                    Each selected lead is linked to the programme with the status below. Leads
                    already on it just get their status updated.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={submitAssign} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Programme</Label>
                    <Select value={assignEvent} onValueChange={setAssignEvent}>
                      <SelectTrigger data-testid="bulk-assign-event-select">
                        <SelectValue placeholder="Choose a programme" />
                      </SelectTrigger>
                      <SelectContent>
                        {events.map((e) => (
                          <SelectItem key={e.id} value={e.id}>
                            {e.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Initial status</Label>
                    <Select value={assignStatus} onValueChange={setAssignStatus}>
                      <SelectTrigger data-testid="bulk-assign-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <DialogFooter>
                    <Button type="submit" disabled={assigning} data-testid="bulk-assign-submit-btn">
                      {assigning ? "Adding…" : "Add to programme"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={priorityOpen} onOpenChange={setPriorityOpen}>
              <DialogContent className="sm:max-w-md" data-testid="bulk-priority-dialog">
                <DialogHeader>
                  <DialogTitle>
                    Set priority on {selected.size} lead{selected.size === 1 ? "" : "s"}
                  </DialogTitle>
                  <DialogDescription>
                    Every selected lead is set to the priority you choose here.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={submitPriority} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select value={bulkPriority} onValueChange={setBulkPriority}>
                      <SelectTrigger data-testid="bulk-priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORITIES.map((p) => {
                          const cfg = PRIORITY_CONFIG[p];
                          const Icon = cfg.icon;
                          return (
                            <SelectItem key={p} value={p}>
                              <span className="flex items-center gap-1.5">
                                <Icon className="h-3.5 w-3.5" /> {cfg.label}
                              </span>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <DialogFooter>
                    <Button type="submit" disabled={settingPriority} data-testid="bulk-priority-submit-btn">
                      {settingPriority ? "Setting…" : "Set priority"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      {/* Filters */}
      <Card className="mt-8 border-border p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or phone…"
              className="pl-9"
              data-testid="leads-search"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={eventId} onValueChange={setEventId}>
              <SelectTrigger className="w-[200px]" data-testid="leads-event-filter">
                <SelectValue placeholder="All programmes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All programmes</SelectItem>
                <SelectItem value="unassigned">Not on any programme</SelectItem>
                {events.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={status}
              onValueChange={setStatus}
              disabled={eventId === "all" || eventId === "unassigned"}
            >
              <SelectTrigger className="w-[160px]" data-testid="leads-status-filter">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-[160px]" data-testid="leads-priority-filter">
                <SelectValue placeholder="All priorities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All priorities</SelectItem>
                {PRIORITIES.map((p) => {
                  const cfg = PRIORITY_CONFIG[p];
                  const Icon = cfg.icon;
                  return (
                    <SelectItem key={p} value={p}>
                      <span className="flex items-center gap-1.5">
                        <Icon className="h-3.5 w-3.5" /> {cfg.label}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
              <SelectTrigger className="w-[140px]" data-testid="leads-page-size">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZES.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n} per page
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="mt-4 border-border shadow-sm" data-testid="leads-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10 px-6">
                <Checkbox
                  checked={filtered.length > 0 && selected.size === filtered.length}
                  onCheckedChange={toggleSelectAll}
                  aria-label="Select all leads on this page"
                  title="Select all leads on this page"
                  data-testid="leads-select-all"
                />
              </TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Name</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Priority</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Phone</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Source</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Events</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">
                {eventId !== "all" ? "Event status" : ""}
              </TableHead>
              <TableHead className="text-right text-xs uppercase tracking-widest">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="p-12 text-center text-sm text-muted-foreground">
                  No leads. Import a list or add one manually.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((l) => {
              const prio = PRIORITY_CONFIG[l.priority] || PRIORITY_CONFIG.medium;
              return (
              <TableRow
                key={l.id}
                data-state={selected.has(l.id) ? "selected" : undefined}
                className={`border-l-2 ${prio.border}`}
              >
                <TableCell className="px-6">
                  <Checkbox
                    checked={selected.has(l.id)}
                    onCheckedChange={() => toggleSelected(l.id)}
                    aria-label={`Select ${l.name}`}
                    data-testid={`lead-select-${l.id}`}
                  />
                </TableCell>
                <TableCell className="py-4 font-semibold">
                  <Link to={`/leads/${l.id}`} className="hover:text-primary hover:underline">
                    {l.name}
                  </Link>
                </TableCell>
                <TableCell className="py-4">
                  <PriorityBadge priority={l.priority} />
                </TableCell>
                <TableCell className="font-mono text-xs">{l.phone}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{l.source}</TableCell>
                <TableCell className="text-xs">{l.events_count || 0}</TableCell>
                <TableCell className="py-4">
                  {l.event_status ? (
                    <Badge className={`${STATUS_TONES[l.event_status]} rounded-full`}>
                      {l.event_status}
                    </Badge>
                  ) : null}
                </TableCell>
                <TableCell className="py-4 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => startEdit(l)}
                      title="Edit lead"
                      data-testid={`lead-edit-${l.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <ConfirmDialog
                      testId={`lead-delete-dialog-${l.id}`}
                      title={`Delete ${l.name}?`}
                      description={
                        "This removes the lead, their whole WhatsApp conversation and their " +
                        "programme links. This cannot be undone."
                      }
                      onConfirm={() => removeLead(l)}
                      trigger={
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Delete lead"
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                          data-testid={`lead-delete-${l.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      }
                    />
                  </div>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {total > 0 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
          <div data-testid="leads-page-summary">
            Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              data-testid="leads-page-prev"
            >
              <ChevronLeft className="mr-1 h-3.5 w-3.5" /> Previous
            </Button>
            <span className="text-xs" data-testid="leads-page-indicator">
              Page {page} of {Math.max(1, Math.ceil(total / pageSize))}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => (p * pageSize < total ? p + 1 : p))}
              disabled={page * pageSize >= total}
              data-testid="leads-page-next"
            >
              Next <ChevronRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
